import React from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { color, space, font, radius, shadow, zIndex } from './tokens';

// ---------------------------------------------------------------------------
// Tooltip — Wave 4.1.A
//
// Thin wrapper over @radix-ui/react-tooltip. Radix handles a11y (role,
// keyboard, screen reader), positioning, and collision detection. We
// supply the visual styling using our tokens.
//
// Usage:
//   <Tooltip content="Re-fire this delivery">
//     <button>↻</button>
//   </Tooltip>
//
// IMPORTANT: wrap your app once with <TooltipProvider> (re-exported below) —
// place it near the root, alongside the existing ToastProvider you set up
// in Wave 4.1.A.
// ---------------------------------------------------------------------------

export interface TooltipProps {
  content:   React.ReactNode;
  /** Show after this delay (ms). Default 150ms — feels intentional, not snappy. */
  delayMs?:  number;
  side?:     'top' | 'right' | 'bottom' | 'left';
  align?:    'start' | 'center' | 'end';
  children:  React.ReactNode;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  delayMs = 150,
  side    = 'top',
  align   = 'center',
  children,
}) => {
  // If there's no content, just render the child unchanged so consumers
  // can pass conditional content without crashing.
  if (!content) return <>{children}</>;

  return (
    <RadixTooltip.Root delayDuration={delayMs}>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          align={align}
          sideOffset={6}
          style={{
            background:   '#111827',
            color:        '#ffffff',
            fontSize:     font.size.sm,
            padding:      `${space[1]}px ${space[2]}px`,
            borderRadius: radius.sm,
            boxShadow:    shadow.md,
            zIndex:       zIndex.tooltip,
            maxWidth:     280,
            lineHeight:   font.lineHeight.snug,
          }}
        >
          {content}
          <RadixTooltip.Arrow style={{ fill: '#111827' }} />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
};

export const TooltipProvider: React.FC<{ children: React.ReactNode; delayDuration?: number }> = ({
  children,
  delayDuration = 150,
}) => (
  <RadixTooltip.Provider delayDuration={delayDuration}>{children}</RadixTooltip.Provider>
);

// re-export for convenience — using just `color` to silence unused import
// (we keep the import because future variants of Tooltip will use it).
void color;

export default Tooltip;
